import { FC } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { IntegrationType, RefreshOrderSettingsViewModel, WaterfallViewModel } from '../../models/waterfall';

interface RefreshOrderSettingsFormProps {
  settings: RefreshOrderSettingsViewModel | null | undefined;
  onChange: (settings: RefreshOrderSettingsViewModel) => void;
  waterfalls: WaterfallViewModel[];
  /** Integration type of the waterfall being edited. Chainable waterfalls are limited to this type. */
  integrationType: IntegrationType;
  disabled?: boolean;
  alwaysSendParentWaterfallDetailInWebhookEvents: boolean;
  onAlwaysSendParentDetailChange: (value: boolean) => void;
  /** Whether to show the Retained Payroll Data section (only for refresh-capable, user-interactive providers). */
  showRetainedDataSettings?: boolean;
}

export const MAXIMUM_DATA_AGE_VALIDATION_MESSAGE = 'Enter a whole number of days greater than 0.';

/** A retained-data configuration is valid when the toggle is off, or the max age is a whole number greater than 0. */
export const isRetainedDataConfigValid = (settings: RefreshOrderSettingsViewModel | null | undefined): boolean => {
  if (!settings?.useRetainedDataBeforeReconnect) return true;
  const { maximumDataAgeDays } = settings;
  return maximumDataAgeDays != null && Number.isInteger(maximumDataAgeDays) && maximumDataAgeDays > 0;
};

export const RefreshOrderSettingsForm: FC<RefreshOrderSettingsFormProps> = ({
  settings,
  onChange,
  waterfalls,
  integrationType,
  disabled = false,
  alwaysSendParentWaterfallDetailInWebhookEvents,
  onAlwaysSendParentDetailChange,
  showRetainedDataSettings = false,
}) => {
  const waterfallOptions = waterfalls
    .filter((w) => !w.archived && w.integrationType === integrationType)
    .map((w) => ({
      label: w.name,
      value: w.waterfallId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleTimeoutChange = (value: number | null) => {
    onChange({
      ...settings,
      timeoutInMinutes: value,
    });
  };

  const handleOnCancelWaterfallChange = (value: number | null) => {
    onChange({
      ...settings,
      onCancelWaterfallId: value,
    });
  };

  const handleOnDisconnectWaterfallChange = (value: number | null) => {
    onChange({
      ...settings,
      onDisconnectWaterfallId: value,
    });
  };

  const handleOnUpgradeFailedWaterfallChange = (value: number | null) => {
    onChange({
      ...settings,
      onUpgradeFailedWaterfallId: value,
    });
  };

  const handleUseRetainedDataChange = (value: boolean) => {
    onChange({
      ...settings,
      useRetainedDataBeforeReconnect: value,
      maximumDataAgeDays: value ? settings?.maximumDataAgeDays ?? null : null,
      suppressDisconnectNotificationsWithinRetainedDataWindow: value
        ? settings?.suppressDisconnectNotificationsWithinRetainedDataWindow ?? false
        : false,
    });
  };

  const handleMaximumDataAgeDaysChange = (value: number | null) => {
    onChange({
      ...settings,
      maximumDataAgeDays: value,
    });
  };

  const handleSuppressDisconnectNotificationsChange = (value: boolean) => {
    onChange({
      ...settings,
      suppressDisconnectNotificationsWithinRetainedDataWindow: value,
    });
  };

  const useRetainedData = settings?.useRetainedDataBeforeReconnect ?? false;
  const showMaximumDataAgeError = useRetainedData && !isRetainedDataConfigValid(settings);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <h3 className="text-lg font-medium mb-4">Refresh Order Settings</h3>
      <p className="text-sm text-gray-600 mb-4">
        Configure timeout and waterfall chaining behavior for refresh orders created from this waterfall.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="field">
          <label htmlFor="refreshTimeoutInMinutes" className="font-medium block mb-2">
            Timeout (Minutes)
          </label>
          <InputNumber
            id="refreshTimeoutInMinutes"
            value={settings?.timeoutInMinutes ?? null}
            onValueChange={(e) => handleTimeoutChange(e.value ?? null)}
            placeholder="e.g., 1440 for 24 hours"
            className="w-full"
            disabled={disabled}
            useGrouping={false}
            min={1}
          />
          <small className="text-gray-500">
            Time before refresh order is auto-cancelled
          </small>
        </div>
        <div className="field">
          <label htmlFor="refreshOnCancelWaterfallId" className="font-medium block mb-2">
            On Cancel Waterfall
          </label>
          <Dropdown
            id="refreshOnCancelWaterfallId"
            value={settings?.onCancelWaterfallId ?? null}
            onChange={(e) => handleOnCancelWaterfallChange(e.value)}
            options={waterfallOptions}
            placeholder="Select waterfall"
            className="w-full"
            disabled={disabled}
            showClear
            appendTo={document.body}
          />
          <small className="text-gray-500">
            Waterfall to execute when refresh order is cancelled
          </small>
        </div>
        <div className="field">
          <label htmlFor="refreshOnDisconnectWaterfallId" className="font-medium block mb-2">
            On Disconnect Waterfall
          </label>
          <Dropdown
            id="refreshOnDisconnectWaterfallId"
            value={settings?.onDisconnectWaterfallId ?? null}
            onChange={(e) => handleOnDisconnectWaterfallChange(e.value)}
            options={waterfallOptions}
            placeholder="Select waterfall"
            className="w-full"
            disabled={disabled}
            showClear
            appendTo={document.body}
          />
          <small className="text-gray-500">
            Waterfall to execute when refresh order is disconnected
          </small>
        </div>
        <div className="field">
          <label htmlFor="refreshOnUpgradeFailedWaterfallId" className="font-medium block mb-2">
            On Upgrade Failed Waterfall
          </label>
          <Dropdown
            id="refreshOnUpgradeFailedWaterfallId"
            value={settings?.onUpgradeFailedWaterfallId ?? null}
            onChange={(e) => handleOnUpgradeFailedWaterfallChange(e.value)}
            options={waterfallOptions}
            placeholder="Select waterfall"
            className="w-full"
            disabled={disabled}
            showClear
            appendTo={document.body}
          />
          <small className="text-gray-500">
            Waterfall to execute when an upgrade of an order from this waterfall fails or returns no data
          </small>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="alwaysSendParentWaterfallDetailInWebhookEvents"
            checked={alwaysSendParentWaterfallDetailInWebhookEvents}
            onChange={(e) => onAlwaysSendParentDetailChange(e.checked ?? false)}
            disabled={disabled}
          />
          <label htmlFor="alwaysSendParentWaterfallDetailInWebhookEvents" className="font-medium cursor-pointer">
            Always Send Parent Waterfall Detail in Webhook Events
          </label>
        </div>
        <small className="text-gray-500 ml-6">
          When enabled, webhook events will include the root parent waterfall detail instead of the child waterfall detail.
        </small>
      </div>
      {showRetainedDataSettings && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h4 className="text-md font-medium mb-2">Retained Payroll Data</h4>
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="useRetainedDataBeforeReconnect"
              checked={useRetainedData}
              onChange={(e) => handleUseRetainedDataChange(e.checked ?? false)}
              disabled={disabled}
            />
            <label htmlFor="useRetainedDataBeforeReconnect" className="font-medium cursor-pointer">
              Use Retained Payroll Data Before Reconnect
            </label>
          </div>
          <small className="text-gray-500 ml-6 block">
            When enabled, eligible retained payroll data is used to generate a VOE before requiring borrower reconnection.
          </small>
          <div className="field mt-4 ml-6 max-w-xs">
            <label htmlFor="maximumDataAgeDays" className="font-medium block mb-2">
              Maximum Data Age (Days)
            </label>
            <InputNumber
              id="maximumDataAgeDays"
              value={settings?.maximumDataAgeDays ?? null}
              onValueChange={(e) => handleMaximumDataAgeDaysChange(e.value ?? null)}
              placeholder="e.g., 35"
              className={showMaximumDataAgeError ? 'w-full p-invalid' : 'w-full'}
              disabled={disabled || !useRetainedData}
              useGrouping={false}
              min={1}
            />
            {showMaximumDataAgeError && (
              <small className="p-error block mt-1">{MAXIMUM_DATA_AGE_VALIDATION_MESSAGE}</small>
            )}
          </div>
          <div className="flex align-items-center gap-2 mt-4">
            <Checkbox
              inputId="suppressDisconnectNotificationsWithinRetainedDataWindow"
              checked={settings?.suppressDisconnectNotificationsWithinRetainedDataWindow ?? false}
              onChange={(e) => handleSuppressDisconnectNotificationsChange(e.checked ?? false)}
              disabled={disabled || !useRetainedData}
            />
            <label htmlFor="suppressDisconnectNotificationsWithinRetainedDataWindow" className="font-medium cursor-pointer">
              Suppress Disconnect Notifications within retained data window
            </label>
          </div>
          <small className="text-gray-500 ml-6 block">
            When enabled, disconnect webhook notifications are not sent while the account&apos;s retained payroll data is still within the window above. Once the data ages out (e.g. on a refresh order), disconnect notifications are sent as normal.
          </small>
        </div>
      )}
    </div>
  );
};
